// Study Hall dialogs (DESIGN.md 5.4, 8.3, 16.2): native <dialog>s for the one-time gate (rules and
// age), the handle picker, reports, confirmations and plain notes. Buttons, not <form>s: the page's
// CSP has form-action 'none'. Every string goes in as text.
import * as C from "./copy.js";
import { h, trustLine } from "./view.js";

let seq = 0;

// {title, body: nodes, actions: [{label, primary, danger, run(api)}], dismiss, cls} -> api
export function modal({ title, body = [], actions = [], dismiss = true, cls = "" }) {
	const id = "rv-hall-dlg-" + ++seq;
	const err = h("p", { class: "rv-hall-dlg-err", role: "alert", hidden: true });
	const buttons = actions.map((a) => h("button", { type: "button", class: "rv-btn" + (a.primary ? " rv-btn-primary" : "") + (a.danger ? " rv-btn-danger" : ""), text: a.label, onclick: () => a.run(api) }));
	const dlg = h("dialog", { class: "rv-hall-dlg " + cls, "aria-labelledby": id }, h("h2", { id, text: title }), body, err, buttons.length ? h("div", { class: "rv-hall-dlg-acts" }, buttons) : null);
	const api = {
		dlg,
		close() { try { dlg.close(); } catch (e) { void e; } },
		error(msg) { err.textContent = msg || ""; err.hidden = !msg; },
		busy(on) { for (const b of buttons) b.disabled = !!on; },
	};
	// Focus goes back where it came from. The opener can be a message toolbar that hid itself
	// meanwhile, so fall back to the nearest focusable thing around it.
	const opener = document.activeElement;
	const openerRow = opener && opener.closest ? opener.closest(".rv-hall-row[data-id]") : null;
	const rowId = openerRow ? openerRow.dataset.id : "";
	dlg.addEventListener("cancel", (e) => { if (!dismiss) e.preventDefault(); });
	dlg.addEventListener("close", () => {
		dlg.remove();
		if (!opener || opener === document.body) return;
		if (!opener.isConnected) {
			// The message list re-rendered (after a report or a delete): go back to the same message,
			// or to the list if it is gone.
			const row = rowId && document.querySelector('.rv-hall-row[data-id="' + CSS.escape(rowId) + '"]');
			const to = row || document.querySelector(".rv-hall-log");
			if (to) { if (!to.hasAttribute("tabindex")) to.setAttribute("tabindex", "-1"); to.focus(); }
			return;
		}
		try { opener.focus(); } catch (e) { void e; }
		if (document.activeElement !== opener) { const near = opener.closest("[tabindex], .rv-hall-row, main"); if (near && near.focus) { if (!near.hasAttribute("tabindex")) near.setAttribute("tabindex", "-1"); near.focus(); } }
	});
	document.body.appendChild(dlg);
	dlg.showModal();
	return api;
}

export function confirmDialog(text, okLabel = C.OK, danger = false, detail = "") {
	return new Promise((resolve) => {
		let done = false;
		const finish = (v, api) => { if (done) return; done = true; resolve(v); api.close(); };
		const api = modal({
			title: text, cls: "rv-hall-dlg-sm", body: detail ? [h("p", { text: detail })] : [],
			actions: [{ label: C.CANCEL, run: (a) => finish(false, a) }, { label: okLabel, primary: !danger, danger, run: (a) => finish(true, a) }],
		});
		api.dlg.addEventListener("close", () => { if (!done) { done = true; resolve(false); } });
	});
}

export function infoDialog(title, lines) {
	return modal({ title, body: lines.filter(Boolean).map((t) => h("p", { text: t })), actions: [{ label: C.OK, primary: true, run: (a) => a.close() }] });
}

export function rulesList() { return h("ol", { class: "rv-hall-rules" }, C.RULES.map((r) => h("li", { text: r }))); }

// Once per account: birth month and year (no preselected value, no hint about which answer works),
// the five rules, and one "I agree". Only the resulting age band is stored, on the server.
export function gateDialog(onSubmit, now = new Date()) {
	const month = h("select", { class: "rv-hall-select", "aria-label": C.MONTH }, h("option", { value: "", text: C.MONTH }), C.MONTHS.map((m, i) => h("option", { value: String(i + 1), text: m })));
	const year = h("select", { class: "rv-hall-select", "aria-label": C.YEAR }, h("option", { value: "", text: C.YEAR }));
	for (let y = now.getFullYear(); y >= now.getFullYear() - 100; y--) year.append(h("option", { value: String(y), text: String(y) }));
	month.value = ""; year.value = "";
	const api = modal({
		title: C.RULES_TITLE, dismiss: false, cls: "rv-hall-gate",
		body: [
			h("fieldset", { class: "rv-hall-age" }, h("legend", { text: C.AGE_PROMPT }), month, year),
			rulesList(),
			trustLine(),
		],
		actions: [{
			label: C.AGREE, primary: true,
			run: (a) => {
				if (!month.value || !year.value) { a.error(C.AGE_PROMPT); return; }
				a.error(""); a.busy(true);
				onSubmit({ birthMonth: Number(month.value), birthYear: Number(year.value), rules: 1 }, a);
			},
		}],
	});
	return api;
}

// Pick (or change) the name everyone sees. Shows the server's reason when it refused one.
export function handleDialog({ current = "", reason = "", dismiss = false, onSubmit }) {
	const input = h("input", { class: "rv-hall-field", type: "text", maxlength: "24", autocomplete: "off", spellcheck: "false", "aria-label": C.HANDLE_LABEL, value: current });
	const run = (a) => {
		const v = input.value.trim();
		if (v.length < 2 || v.length > 24) { a.error(C.HANDLE_HINT); return; }
		a.error(""); a.busy(true);
		onSubmit(v, a);
	};
	const actions = [{ label: C.SAVE, primary: true, run }];
	if (dismiss) actions.unshift({ label: C.CANCEL, run: (a) => a.close() });
	const api = modal({ title: C.HANDLE_TITLE, dismiss, body: [h("label", { class: "rv-hall-lbl" }, h("span", { text: C.HANDLE_LABEL }), input), h("p", { class: "rv-hall-muted", text: C.HANDLE_HINT })], actions });
	if (reason) api.error(reason);
	input.addEventListener("keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); run(api); } });
	input.focus();
	return api;
}

// Categories, an optional note (300 characters) and "Also block this person" (off by default).
export function reportDialog({ canBlock, onSubmit }) {
	const name = "rv-hall-rc-" + ++seq;
	const radios = C.REPORT_CATS.map(([v, label]) => h("label", { class: "rv-hall-radio" }, h("input", { type: "radio", name, value: v }), h("span", { text: label })));
	const note = h("textarea", { class: "rv-hall-field", maxlength: "300", rows: "3", "aria-label": C.REPORT_NOTE, placeholder: C.REPORT_NOTE });
	const block = h("input", { type: "checkbox" });
	return modal({
		title: C.REPORT_TITLE,
		body: [h("div", { class: "rv-hall-radios", role: "radiogroup", "aria-label": C.REPORT_TITLE }, radios), note, canBlock ? h("label", { class: "rv-hall-check" }, block, h("span", { text: C.REPORT_BLOCK })) : null],
		actions: [
			{ label: C.CANCEL, run: (a) => a.close() },
			{
				label: C.REPORT_SEND, primary: true,
				run: (a) => {
					const picked = radios.map((l) => l.querySelector("input")).find((i) => i.checked);
					if (!picked) { a.error(C.REPORT_PICK); return; }
					a.error(""); a.busy(true);
					onSubmit({ cat: picked.value, note: note.value.trim(), block: canBlock && block.checked }, a);
				},
			},
		],
	});
}

// A new poll: a question, 2 to 4 options and how long it stays open ("until this focus block
// ends" only while one runs). The server screens every field like a message.
export function pollDialog({ blockRunning, onSubmit }) {
	const q = h("input", { class: "rv-hall-field", maxlength: "200", "aria-label": C.POLL_Q, placeholder: C.POLL_Q });
	const opts = h("div", { class: "rv-hall-poll-opts" });
	const addOpt = () => {
		const n = opts.children.length;
		if (n >= 4) return;
		opts.append(h("input", { class: "rv-hall-field", maxlength: "80", "aria-label": C.POLL_OPT(n), placeholder: C.POLL_OPT(n) }));
		add.hidden = opts.children.length >= 4;
	};
	const add = h("button", { type: "button", class: "rv-hall-link", text: C.POLL_ADD, onclick: addOpt });
	addOpt(); addOpt();
	const dur = h("select", { class: "rv-hall-select", "aria-label": C.POLL_LENGTH },
		C.POLL_DURS.filter(([m]) => m || blockRunning).map(([m, label]) => h("option", { value: String(m), text: label })));
	dur.value = "15";
	return modal({
		title: C.NEW_POLL,
		body: [q, opts, add, h("label", { class: "rv-hall-poll-dur" }, h("span", { text: C.POLL_LENGTH }), dur), h("p", { class: "rv-hall-muted", text: C.POLL_PRIVATE })],
		actions: [
			{ label: C.CANCEL, run: (a) => a.close() },
			{
				label: C.POLL_POST, primary: true,
				run: (a) => {
					const o = [...opts.children].map((i) => i.value.trim()).filter(Boolean);
					if (!q.value.trim() || o.length < 2) { a.error(C.POLL_NEED); return; }
					a.error(""); a.busy(true);
					onSubmit({ q: q.value.trim(), o, dur: Number(dur.value) }, a);
				},
			},
		],
	});
}

// Someone's name card: their name and, if they show it, one of three status labels.
export function nameCard({ h: handle, label, staffButton }) {
	const status = h("p", { class: "rv-hall-pres", title: C.PRES_TIP, text: label || C.PRES_NONE, "data-on": label ? "1" : "" });
	return modal({
		title: handle, cls: "rv-hall-dlg-sm rv-hall-namecard",
		body: [status, h("p", { class: "rv-hall-muted", text: C.PRES_TIP })],
		actions: [...(staffButton ? [staffButton] : []), { label: C.CLOSE, primary: true, run: (a) => a.close() }],
	});
}
